import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import {
  UserX,
  Search,
  ChevronDown,
  User,
  Mail,
  Phone,
  Tag,
  Calendar,
  X,
  Shield,
  MessageSquare,
} from 'lucide-react';

// Reason codes emitted by the apps (web + Flutter) for account deletion.
const REASON_META = {
  no_longer_needed: { label: 'No longer needed', color: '#3B82F6' },
  privacy: { label: 'Privacy concerns', color: '#A855F7' },
  found_alternative: { label: 'Found alternative', color: '#F59E0B' },
  bad_experience: { label: 'Bad experience', color: '#EF4444' },
  other: { label: 'Other', color: '#6B7280' },
};

const AccountDeletions = () => {
  const { colors } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasonFilter, setReasonFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'accountDeletions'), orderBy('deletedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            deletedAt: d.data().deletedAt?.toDate(),
          }))
        );
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  const reasonInfo = (code) =>
    REASON_META[code] || { label: code || 'Unknown', color: colors.textMuted };

  const filtered = items.filter((m) => {
    const matchReason = reasonFilter === 'all' || m.reasonCode === reasonFilter;
    const term = search.toLowerCase();
    const matchSearch =
      !term ||
      m.userName?.toLowerCase().includes(term) ||
      m.userEmail?.toLowerCase().includes(term) ||
      m.userPhone?.includes(term) ||
      m.details?.toLowerCase().includes(term);
    return matchReason && matchSearch;
  });

  const fmt = (date) =>
    date
      ? date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  return (
    <div className="p-6 max-w-7xl mx-auto" style={{ color: colors.text }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>
            Account Deletions
          </h1>
          <p className="text-sm mt-0.5" style={{ color: colors.textMuted }}>
            {items.length} account{items.length === 1 ? '' : 's'} deleted — reasons and notes left by users
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl flex-1 min-w-[200px]"
          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <Search className="w-4 h-4" style={{ color: colors.textMuted }} />
          <input
            className="bg-transparent outline-none text-sm w-full"
            style={{ color: colors.text }}
            placeholder="Search by name, email, phone, note..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="relative">
          <select
            className="appearance-none pl-3 pr-8 py-2 rounded-xl text-sm font-medium cursor-pointer"
            style={{
              backgroundColor: colors.surface,
              color: colors.text,
              border: `1px solid ${colors.border}`,
            }}
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
          >
            <option value="all">All Reasons</option>
            {Object.entries(REASON_META).map(([key, meta]) => (
              <option key={key} value={key}>
                {meta.label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: colors.textMuted }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {Object.entries(REASON_META).map(([key, meta]) => {
          const count = items.filter((m) => m.reasonCode === key).length;
          return (
            <div
              key={key}
              className="flex items-center gap-3 p-4 rounded-xl cursor-pointer"
              style={{
                backgroundColor: reasonFilter === key ? meta.color + '1f' : colors.surface,
                border: `1px solid ${reasonFilter === key ? meta.color + '40' : colors.border}`,
              }}
              onClick={() => setReasonFilter(reasonFilter === key ? 'all' : key)}
            >
              <div>
                <p className="text-xl font-bold" style={{ color: meta.color }}>{count}</p>
                <p className="text-xs" style={{ color: colors.textMuted }}>{meta.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div
            className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: colors.primary }}
          />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20" style={{ color: colors.textMuted }}>
          <UserX className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No deleted accounts found</p>
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
                {['User', 'Reason', 'Note', 'Deleted', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider"
                    style={{ color: colors.textMuted }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => {
                const meta = reasonInfo(m.reasonCode);
                return (
                  <tr
                    key={m.id}
                    className="cursor-pointer transition-colors"
                    style={{
                      backgroundColor: i % 2 === 0 ? colors.background : colors.surface,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                    onClick={() => setSelected(m)}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primaryGhost)}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = i % 2 === 0 ? colors.background : colors.surface)
                    }
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: colors.text }}>
                        {m.userName || '—'}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                        {m.userEmail || m.userPhone || m.userId}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: meta.color + '1f', color: meta.color }}
                      >
                        <Tag className="w-3 h-3" />
                        {m.reasonLabel || meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: colors.textMuted }}>
                      <div className="text-xs truncate max-w-[240px]">{m.details || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: colors.textMuted }}>
                      {fmt(m.deletedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                        style={{ backgroundColor: colors.primaryGhost, color: colors.primary }}
                        onClick={(e) => { e.stopPropagation(); setSelected(m); }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-end"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSelected(null)}
        >
          <div
            className="h-full w-full max-w-md overflow-y-auto flex flex-col"
            style={{ backgroundColor: colors.background }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-6 py-4 sticky top-0"
              style={{ backgroundColor: colors.background, borderBottom: `1px solid ${colors.border}` }}
            >
              <h2 className="font-bold text-lg" style={{ color: colors.text }}>
                Deletion Detail
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: colors.textMuted }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-5">
              {/* User info */}
              <div
                className="rounded-2xl p-4 space-y-3"
                style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
              >
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4" style={{ color: colors.textMuted }} />
                  <span style={{ color: colors.text }}>{selected.userName || '—'}</span>
                </div>
                {selected.userEmail && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4" style={{ color: colors.textMuted }} />
                    <span style={{ color: colors.text }}>{selected.userEmail}</span>
                  </div>
                )}
                {selected.userPhone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4" style={{ color: colors.textMuted }} />
                    <span style={{ color: colors.text }}>{selected.userPhone}</span>
                  </div>
                )}
                {selected.userRole && (
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4" style={{ color: colors.textMuted }} />
                    <span style={{ color: colors.text }}>{selected.userRole}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4" style={{ color: colors.textMuted }} />
                  <span style={{ color: colors.textMuted }}>{fmt(selected.deletedAt)}</span>
                </div>
                <div className="flex items-center gap-2 text-xs pt-1" style={{ color: colors.textMuted }}>
                  <span>UID: {selected.userId}</span>
                  {selected.platform && <span>· {selected.platform}</span>}
                </div>
              </div>

              {/* Reason */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4" style={{ color: colors.textMuted }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                    Reason
                  </span>
                </div>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                  style={{
                    backgroundColor: reasonInfo(selected.reasonCode).color + '1f',
                    color: reasonInfo(selected.reasonCode).color,
                  }}
                >
                  {selected.reasonLabel || reasonInfo(selected.reasonCode).label}
                </span>
              </div>

              {/* Note */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-4 h-4" style={{ color: colors.textMuted }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                    User Note
                  </span>
                </div>
                <p
                  className="text-sm leading-relaxed p-4 rounded-xl"
                  style={{
                    color: colors.text,
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  {selected.details || 'No additional note provided.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDeletions;
